/**
 * The second recording on a call that has two, and the block that says why
 * there are two.
 *
 * A call the AI answered, handed on, and that nobody picked up ends with a
 * message the caller left. Both halves are recordings of the same call and the
 * row has one slot for them: the message wins it, because the message is the
 * half somebody has to answer and everything downstream - the transcript above,
 * the summary, the ticket - is built from the slot. The conversation the machine
 * had is kept on the shelf (`agentRecordingKey`), and this is the only place in
 * the product that can reach it.
 *
 * Drawn UNDER the main player and plainly labelled, never beside it as an equal.
 * Two unlabelled players on one call is the question "which of these is the
 * call?" asked of the operator, and the pane already answers it: the first one
 * is what the caller said to us.
 */
import { useState } from "react";
import { Bot, ChevronDown } from "lucide-react";
import { Button, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { CallDetail } from "../../../lib/calls";
import { RecordingPlayer } from "./RecordingPlayer";

export function AgentRecording({ call, canHear }: { call: CallDetail; canHear: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);

  // `recordingStatus` gates this as well as the player above, and that is not a
  // spare check: an erase and a retention sweep take the pair together, so a
  // purged call must not keep offering the conversation.
  //
  // `canHear` is the same line the transcript and the follow-up letter are on.
  // The route would answer this viewer with a 404 anyway; offering a player
  // that cannot play is a dead control, which is worse than no control.
  if (!canHear || !call.agentRecordingKey || call.recordingStatus !== "stored") return null;

  const text = (call.agentTranscriptText ?? "").trim();

  return (
    <div className="flex-none border-b border-[var(--crm-hair)] px-6 py-4">
      <div className="mb-2 flex items-center gap-2 text-[12.75px] text-muted-foreground">
        <Bot className="size-4" />
        <span className="font-medium">{t("calls.agentRecording.title")}</span>
      </div>
      {/* Said every time rather than only on first sight: this is the one block
          in the pane whose existence needs explaining, and an operator meets it
          once every few months. */}
      <p className="mb-3 text-[12.25px] leading-relaxed text-muted-foreground">
        {t("calls.agentRecording.hint")}
      </p>
      <RecordingPlayer
        callId={call.id}
        duration={call.agentRecordingSec}
        part="agent"
        /* No peaks: the row's waveform belongs to the message in the slot, and
           drawing it here would put the message's shape under the
           conversation's audio. The player falls back to its own hash, which is
           what it does for every recording nobody measured. */
      />
      {text ? (
        <div className="mt-3">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="h-auto gap-1.5 px-0 py-0 text-[12.25px] font-normal text-muted-foreground hover:bg-transparent hover:text-foreground [&_svg]:size-3.5"
          >
            <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
            {t("calls.agentRecording.transcript")}
          </Button>
          {open ? (
            <p className="mt-2 whitespace-pre-wrap text-[12.75px] leading-relaxed">{text}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
